import React, { useState, useEffect } from 'react';
import './Validate.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PDFViewer from './PDFViewer';
import './PDFViewer.css';
import OmicsViewModal from './OmicsView/OmicsView';
import { 
    faSearch, 
    faFlask, 
    faCheckCircle, 
    faDatabase,
    faCog,
    faVial,
    faDna,
    faBacteria,
    faTooth,
    faVials,
    faUserShield,
    faOilCan,
    faFlaskVial,
    faDroplet,
    faCircleNodes,
    faAtom,
    faSpinner,
    faExclamationTriangle,
    faCheck,
    faHandPaper,
    faFilePdf,
    faUserCheck,
    faBaby
} from '@fortawesome/free-solid-svg-icons';

function Validate({ user, samples: propSamples }) {
    const [samples, setSamples] = useState([]);
    const [filteredSamples, setFilteredSamples] = useState([]);
    const [selectedSample, setSelectedSample] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedKitType, setSelectedKitType] = useState(null);
    const [validationStatus, setValidationStatus] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showOmicsModal, setShowOmicsModal] = useState(false);
    const [selectedOmicsType, setSelectedOmicsType] = useState(null);
    const [selectedOmicsId, setSelectedOmicsId] = useState(null);
    const [omicsModalData, setOmicsModalData] = useState({});
    const [pdfReloadKey, setPdfReloadKey] = useState(0); // Key to force PDF reload

    const kitTypes = [
        { name: 'Lifelong Health Test', icon: faDna, technicalName: 'Whole Genome' },
        { name: 'Gut Health Test', icon: faBacteria, technicalName: 'Gut Health' },
        { name: 'Oral Health Test', icon: faTooth, technicalName: 'Oral Health' },
        { name: 'Comprehensive Carrier Screening Test', icon: faUserCheck, Settings: true },
        { name: 'Comprehensive Newborn Screening Test', icon: faBaby, Settings: true},

    ];
    
    const getKitTypeCount = (kitType) => {
        const deliveredSamples = samples.filter(sample => 
            sample.status && 
            sample.status.stages && 
            sample.status.stages['Data Delivered']
        );
        
        return deliveredSamples.filter(sample => sample.kitType === kitType).length;
    };
    
    const fetchSamples = async () => {
        setIsLoading(true);
        try {
            const response = await fetch('https://service1.szapfs.org/reqvalidorders');
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const data = await response.json();
            console.log('Validate: Fetched data successfully:', data.length, 'samples');
            
            const transformedData = data.map(order => ({
                id: order.id,
                Email: order.Email,
                patientId: order.patientId || `PAT-${order.barcode.substring(3)}`,
                barcode: order.barcode,
                firstName: order.firstName,
                lastName: order.lastName,
                address: order.address,
                kitType: order.kitType,
                visitNumber: order.visitNumber,
                status: {
                    currentStage: order.currentStage,
                    stages: {
                        "Order Received": order.order_received,
                        "Sent Cargo": order.sent_cargo,
                        "Cargo Delivered": order.cargo_delivered,
                        "Lab Received": order.lab_received,
                        "Data Delivered": order.data_delivered
                    }
                }
            }));
            
            setSamples(transformedData);
            setError(null);
        } catch (err) {
            console.error("Error fetching samples:", err);
            setError("Failed to load samples. Please try again later.");
            
            if (propSamples && propSamples.length > 0) {
                setSamples(propSamples);
            }
        } finally {
            setIsLoading(false);
        }
    };
    
    useEffect(() => {
        fetchSamples();
        
        if (propSamples && propSamples.length > 0 && samples.length === 0) {
            setSamples(propSamples);
        }
    }, [propSamples]);
    
    useEffect(() => {
        if (!samples || samples.length === 0) return;
        
        let filtered = samples.filter(sample => 
            sample.status && 
            sample.status.stages && 
            sample.status.stages['Data Delivered']
        );
        
        if (searchTerm.trim() !== '') {
            const searchTermLower = searchTerm.toLowerCase();
            filtered = filtered.filter(sample => 
                (sample.patientId && sample.patientId.toLowerCase().includes(searchTermLower)) ||
                sample.barcode.toLowerCase().includes(searchTermLower) ||
                sample.firstName.toLowerCase().includes(searchTermLower) ||
                sample.lastName.toLowerCase().includes(searchTermLower) ||
                (sample.kitType && sample.kitType.toLowerCase().includes(searchTermLower))
            );
        }
        
        if (selectedKitType) {
            filtered = filtered.filter(sample => 
                sample.kitType === selectedKitType
            );
        }
        
        setFilteredSamples(filtered);
    }, [samples, searchTerm, selectedKitType]);
    
    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };
    const handleRegenerateClick = async () => {
        if (!selectedSample) return;
        const clinicianId = user.clinicianId || user.id;
        const payload = {
            clinicianId,
            patientId: selectedSample.patientId,
            kitType: selectedSample.kitType,
            visitNumber: selectedSample.visitNumber,
            barcode: selectedSample.barcode,
            firstName: selectedSample.firstName,
            lastName: selectedSample.lastName
        };
        console.log('Regenerate request payload:', payload);
        try {
            // First, increment the reload key to force PDF viewer to reset
            setPdfReloadKey(prevKey => prevKey + 1);
            
            // Then, send the regenerate request
            const response = await fetch('https://service1.szapfs.org/regeneratereport', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            console.log('Regenerate report response:', data);
            
            // Log that we're initiating polling
            console.log(`[${new Date().toLocaleTimeString()}] Regenerate requested - initiating PDF polling`);
            
            // Force another reload immediately after receiving the response
            // This starts the polling process in PDFViewer
            setPdfReloadKey(prevKey => prevKey + 1);
        } catch (err) {
            console.error('Error regenerating report:', err);
            alert(`Failed to regenerate report: ${err.message}`);
        }
    };
    
    const handleKitTypeSelect = (kitType) => {
        if (selectedKitType === kitType) {
            setSelectedKitType(null); 
        } else {
            setSelectedKitType(kitType); 
        }
    };
    
    const handleSampleSelect = (sample) => {
        setSelectedSample(sample);
        console.log(`Validate: Selected sample ${sample.barcode}, will attempt to load PDF for patient ${sample.patientId} with kit type ${sample.kitType}`);
    };
    
    const handleSettingsClick = (e, sample) => {
        e.stopPropagation(); 
        setSelectedSample(sample);

        // Find the technical name for this kit type
        const kitType = kitTypes.find(kt => kt.name === sample.kitType);
        if (kitType && kitType.technicalName) {
            setSelectedOmicsType(kitType.technicalName);
        } else {
            setSelectedOmicsType(sample.kitType);
        }
        
        setSelectedOmicsId(sample.barcode);
        setOmicsModalData(sample);
        setShowOmicsModal(true);
    };
    
    const handleValidateClick = async (sample) => {
        if (!sample) return;
        
        setValidationStatus('validating');
        
        try {
            const clinicianId = user.userId || user.id;
            const barcode = sample.barcode;
            const patientId = sample.patientId || '';
            const kitType = sample.kitType || '';
            const visitNumber = sample.visitNumber || '1';
            const email = sample.Email || '';

            
            const requestPayload = {
                barcode: barcode,
                clinicianId: clinicianId,
                patientId: patientId,
                kitType: kitType,
                visitNumber: visitNumber,
                firstName: sample.firstName,
                lastName: sample.lastName,
                email: email
            };
            
            console.log('Validation request payload:', requestPayload);
            console.log('Validating sample with barcode:', barcode, 'and clinicianId:', clinicianId);
            
            const response = await fetch('https://service1.szapfs.org/validateorder', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestPayload)
            });
            
            if (!response.ok) {
                console.error('Validation response error:', {
                    status: response.status,
                    statusText: response.statusText
                });
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const data = await response.json();
            console.log('Validation successful:', data);
            console.log('Validation response details:', {
                barcode: barcode,
                responseData: data,
                timestamp: new Date().toISOString()
            });
            
            setValidationStatus('validated');
            
            setTimeout(() => {
                setValidationStatus('');
                
                fetchSamples();
                
                setSelectedSample(null);
            }, 2000);
            
        } catch (error) {
            console.error('Error validating sample:', error);
            console.error('Validation failed for barcode:', sample.barcode, 'Error details:', {
                message: error.message,
                stack: error.stack,
                timestamp: new Date().toISOString()
            });
            setValidationStatus('');
            alert(`Failed to validate sample: ${error.message}`);
        }
    };
    
    const getKitTypeIcon = (kitTypeName) => {
        const kitType = kitTypes.find(kt => kt.name === kitTypeName);
        return kitType ? kitType.icon : faFlask;
    };
    
    const isSettingsDisabled = (sampleKitType) => {
        const kitType = kitTypes.find(kt => kt.name === sampleKitType);
        return kitType && kitType.Settings === true;
    };
    
    const getValidateButtonClass = () => {
        if (validationStatus === 'validating') return 'ValidateButton validating';
        if (validationStatus === 'validated') return 'ValidateButton validated';
        return 'ValidateButton';
    };
    
    const getValidateButtonText = () => {
        if (validationStatus === 'validating') return 'Validating...';
        if (validationStatus === 'validated') return 'Validated!';
        return 'Validate Sample';
    };
    
    return (
        <div className="Validate">
            <div className="ValidateLeft">
                <div className="ValidateLeft-top">
                    <div className="ValidateSearchBar">
                        <FontAwesomeIcon icon={faSearch} className="ValidateSearchIcon" />
                        <input 
                            type="text" 
                            placeholder="Search samples..." 
                            value={searchTerm}
                            onChange={handleSearch}
                            className="ValidateSearchInput"
                        />
                    </div>
                    
                    <div className="ValidateKitTypeFilters">
                        {kitTypes.map((kitType, index) => (
                            <button 
                                key={index}
                                className={`ValidateKitTypeButton ${selectedKitType === kitType.name ? 'active' : ''}`}
                                onClick={() => handleKitTypeSelect(kitType.name)}
                            >
                                <FontAwesomeIcon icon={kitType.icon} />
                                <span>{kitType.name}</span>
                                <span className="ValidateKitTypeCount">{getKitTypeCount(kitType.name)}</span>
                            </button>
                        ))}
                    </div>
                </div>
                
                <div className="ValidateLeft-bottom">
                    <div className="ValidateSamplesHeader">
                        <h3>Requires Validation</h3>
                        <span className="ValidateSamplesCount">{filteredSamples.length}</span>
                    </div>
                    
                    <div className="ValidateSamplesList">
                        {isLoading ? (
                            <div className="ValidateLoading">Loading samples...</div>
                        ) : error ? (
                            <div className="ValidateError">{error}</div>
                        ) : filteredSamples.length === 0 ? (
                            <div className="ValidateEmpty">No samples match your criteria</div>
                        ) : (
                            filteredSamples.map((sample) => (
                                <div 
                                    key={sample.barcode}
                                    className={`ValidateSampleItem ${selectedSample && selectedSample.barcode === sample.barcode ? 'selected' : ''}`}
                                    onClick={() => handleSampleSelect(sample)}
                                >
                                    <div className="ValidateSampleMain">
                                        <div className="ValidateSampleIconContainer">
                                            <FontAwesomeIcon icon={getKitTypeIcon(sample.kitType)} className="ValidateSampleIcon" />
                                        </div>
                                        
                                        <div className="ValidateSampleDetails">
                                            <div className="ValidateSampleName">
                                                {sample.firstName} {sample.lastName}
                                            </div>
                                            <div className="ValidateSampleId">
                                                {sample.barcode} • {sample.patientId}

                                            </div>
                                        </div>
                                    </div>

                                    <button 
                                        className="ValidateSampleSettings"
                                        onClick={(e) => handleSettingsClick(e, sample)}
                                        disabled={isSettingsDisabled(sample.kitType)}
                                    >
                                        <FontAwesomeIcon icon={faCog} />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
            
            {selectedSample && (
                <div className="ValidateRight">
                    <div className="ValidateSampleDetailPDF">
                    <PDFViewer 
                        pdfUrl={`https://service1.szapfs.org/api/retrieve-report?patientID=${selectedSample.patientId}&reporttype=${selectedSample.kitType}`}
                        forceReloadKey={pdfReloadKey}
                    />
                    </div>
                    <div className="ValidateSampleDetailFooter">
                    <button 
                        className="RegenerateButton"
                        onClick={handleRegenerateClick}
                    >
                        Regenerate
                    </button>
                    <button 
                        className={getValidateButtonClass()}
                        onClick={() => handleValidateClick(selectedSample)}
                        disabled={validationStatus !== ''}
                    >
                        {getValidateButtonText()}
                    </button>
                    </div>
                </div>
            )}
                        {!selectedSample && (
                <div className="ValidateRight">
                                    <div className="ValidateRightInner">
                                    <FontAwesomeIcon icon={faFilePdf} className="FilePDF" />
                                    <div className="FilePDFText">Please select a sample</div>

                            </div>
                </div>
            )}
            {/* OmicsView Modal */}
            {showOmicsModal && (
                <OmicsViewModal 
                    isOpen={showOmicsModal}
                    onClose={() => setShowOmicsModal(false)}
                    omicsType={selectedOmicsType}
                    omicsId={selectedOmicsId}
                    omicsData={omicsModalData}
                />
            )}
        </div>
    );
}

export default Validate;
